import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { AddRoleGroupRequest, GroupedRoles, RoleGroup, RoleType, UpdateRoleGroupRequest } from '@models/authorization';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  getAll,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { RoleGroupsService } from './role-groups.service';

type RoleGroupsState = {
  roleGroups: RoleGroup[];
  isLoading: boolean;
  roleGroupCreateLoading: boolean;

  selectedRoleGroupId: string | null;

  groupedRoles: GroupedRoles[];
  groupedRolesLoading: boolean;

  roleGroupRoles: string[];
  roleGroupRolesLoading: boolean;
};

const initialState: RoleGroupsState = {
  roleGroups: [],
  isLoading: false,
  roleGroupCreateLoading: false,

  selectedRoleGroupId: null,

  groupedRoles: [],
  groupedRolesLoading: false,

  roleGroupRoles: [],
  roleGroupRolesLoading: false,
};

const roleGroupsDictionary: StateDictionary<RoleGroup> = {
  entityList: 'roleGroups',
  listLoading: 'isLoading',
  selectedEntity: '',
  selectedEntityLoading: '',
  entityId: 'id',
  sortFn: (a, b) => a.name.localeCompare(b.name),
  createLoading: 'roleGroupCreateLoading',
};

export const RoleGroupsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('role-groups-store'),

  withComputed(store => ({
    selectedRoleGroup: computed(() => store.roleGroups().find(roleGroup => roleGroup.id === store.selectedRoleGroupId()) || null),
  })),

  withMethods((store, roleGroupsService = inject(RoleGroupsService), router = inject(Router)) => {
    createStateResetHandler<RoleGroupsState>('/role-groups')(store, initialState);
    createCustomStateSetOnUrlHandler<RoleGroupsState>('/role-groups', { selectedRoleGroupId: null })(store);

    const roleGroupMethods = {
      getAll: getAll<RoleGroup>({
        store,
        service: roleGroupsService,
        dictionary: roleGroupsDictionary,
      }),

      addNewRoleGroup: addEntity<RoleGroup, AddRoleGroupRequest>({
        store,
        service: roleGroupsService,
        dictionary: roleGroupsDictionary,
        onSuccessCallback: (newRoleGroup: RoleGroup) => {
          patchState(store, { selectedRoleGroupId: newRoleGroup.id });
          router.navigate(['role-groups', newRoleGroup.id, 'details']);
        },
      }),

      updateRoleGroup: updateEntity<RoleGroup, UpdateRoleGroupRequest>({
        store,
        service: roleGroupsService,
        dictionary: roleGroupsDictionary,
      }),

      deleteRoleGroup: deleteEntity<RoleGroup>({
        store,
        service: roleGroupsService,
        dictionary: roleGroupsDictionary,
        onSuccessCallback: () => {
          router.navigate(['role-groups']);
          roleGroupMethods.getAll();
        },
      }),
    };

    const roleGroupRolesMethods = {
      getGroupedRoles: rxMethod<RoleType | undefined>(
        pipe(
          tap(() => patchState(store, { groupedRolesLoading: true })),
          switchMap(filter =>
            roleGroupsService.getGroupedRoles(filter).pipe(
              tap(groupedRoles => {
                patchState(store, {
                  groupedRolesLoading: false,
                  groupedRoles,
                });
              }),
              catchError(() => {
                patchState(store, { groupedRolesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getRoleGroupRoles: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { roleGroupRolesLoading: true })),
          switchMap(roleGroupId =>
            roleGroupsService.getRoleGroupRoles(roleGroupId).pipe(
              tap(roleNames => {
                patchState(store, {
                  roleGroupRolesLoading: false,
                  roleGroupRoles: roleNames,
                });
              }),
              catchError(() => {
                patchState(store, { roleGroupRolesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      selectRoleGroup: (id: string): void => {
        if (!store.roleGroups().length && !store.isLoading()) roleGroupMethods.getAll();
        patchState(store, { selectedRoleGroupId: id });
      },

      goToRoleGroupActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['role-groups', id]);
        if (!result) return false;

        patchState(store, { selectedRoleGroupId: id });
        return true;
      },
    };

    return {
      ...roleGroupMethods,
      ...roleGroupRolesMethods,
      ...navigationActions,
    };
  }),
);

export interface RoleGroupsStoreType {
  readonly roleGroups: Signal<RoleGroup[]>;
  readonly isLoading: Signal<boolean>;
  readonly roleGroupCreateLoading: Signal<boolean>;
  readonly selectedRoleGroup: Signal<RoleGroup | null>;
  readonly groupedRoles: Signal<GroupedRoles[]>;
  readonly groupedRolesLoading: Signal<boolean>;
  readonly roleGroupRoles: Signal<string[]>;
  readonly roleGroupRolesLoading: Signal<boolean>;
  readonly selectedRoleGroupId: Signal<string | null>;

  readonly getAll: () => void;
  readonly addNewRoleGroup: (dto: AddRoleGroupRequest) => void;
  readonly updateRoleGroup: (params: { id: string; dto: UpdateRoleGroupRequest }) => void;
  readonly deleteRoleGroup: (id: string) => void;

  readonly getGroupedRoles: (filter?: RoleType) => void;
  readonly getRoleGroupRoles: (roleGroupId: string) => void;

  readonly goToRoleGroupActions: (id: string) => Promise<boolean>;
  readonly selectRoleGroup: (id: string) => void;
}
